import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import "./genericForm.scss";
import Dropdown from "./Dropdown";
import Switch from "react-switch";
import Button from "./Button";
import { svgs } from "../common/assats";
import PhoneNumberInput from "./PhoneNumberInput/PhoneNumberInput";

export interface Option {
  value: string;
  label: string;
}

export interface FormField {
  label: string;
  name: string;
  type: string;
  options?: Option[]; // for dropdowns if applicable
  validation?: Yup.Schema<any>; // Custom Yup validation schema for the field
  initialValue?: any; // Optional initial value for the field
  value?: boolean; // Used for switch
  disabled?: boolean;
  onClick?: () => void;
}

type FormData = {
  [key: string]: any;
};

interface GenericFormProps {
  fields: FormField[];
  buttonText?: string;
  backButtonText: string;
  backButtonAction: () => void;
  onSubmit?: (formData: FormData) => void;
  children?: React.ReactNode;
  title: string;
  onSwitchChange?: (fieldName: string, checked: boolean) => void;
  onFieldChange?: (fieldName: string, value: any) => void;
}

const GenericForm: React.FC<GenericFormProps> = ({
  title,
  children,
  backButtonAction,
  backButtonText,
  buttonText,
  fields,
  onSubmit,
  onSwitchChange,
  onFieldChange,
}) => {
  const [formData, setFormData] = useState<FormData>({});
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [phone, setPhone] = useState<string>("");


  useEffect(() => {
    const initialValues: FormData = {};

    fields.forEach((field) => {
      if (field.type === "switch") {
        initialValues[field.name] =
          field.value !== undefined
            ? Boolean(field.value)
            : field.initialValue !== undefined
              ? Boolean(field.initialValue)
              : true;
      } else {
        initialValues[field.name] =
          field.value !== undefined
            ? field.value
            : field.initialValue ?? "";
      }
    });

    setFormData(initialValues);
  }, [fields]);


  // Create a Yup validation schema based on fields
  const createValidationSchema = (
    fields: FormField[],
    role: string | string[]
  ) => {
    const schema: { [key: string]: Yup.Schema<any> } = {};

    fields.forEach((field) => {
      if (field.validation) {
        schema[field.name] = field.validation; // Use custom validation if provided
      } else {
        // Default validation based on field type
        switch (field.type) {
          case "text":
            if (field.name === "apartment" || field.name === "socialSecurityNumber") {
              schema[field.name] = Yup.string();
            } else {
              schema[field.name] = Yup.string().required(
                `${field.label} is required`
              );
            }
            break;
          case "email":
            schema[field.name] = Yup.string()
              .email("Invalid email format")
              .required(`${field.label} is required`);
            break;
          case "select":
            // Conditionally exclude "center" field if role is Admin
            if (field.name === "center") {
              // schema[field.name] = Yup.string().when(
              //   "role",
              //   (roleVal, schema) => {
              //     const isAdmin = Array.isArray(roleVal)
              //       ? roleVal.includes("Admin")
              //       : roleVal === "Admin";
              //     return isAdmin
              //       ? schema.notRequired()
              //       : schema.required(`${field.label} is required`);
              //   }
              // );
            } else {
              schema[field.name] = Yup.string().required(
                `${field.label} is required`
              );
            }
            break;
          default:
            break;
        }
      }
    });

    return Yup.object().shape(schema);
  };

  const validationSchema = createValidationSchema(fields, formData.role || ""); // Pass the role to validation schema

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const fieldName = e.target.name;
    const newValue = e.target.value;
    setFormData({
      ...formData,
      [fieldName]: newValue,
    });
    if (onFieldChange) {
      onFieldChange(fieldName, newValue);
    }
  };

  const handleSelectChange = (name: string, selectedOption: Option) => {
    const newValue = selectedOption.value;
    setFormData({
      ...formData,
      [name]: newValue,
    });
    if (onFieldChange) {
      onFieldChange(name, newValue);
    }
  };

  const handleSwitchChange = (name: string, checked: boolean) => {
    setFormData({
      ...formData,
      [name]: checked,
    });
    if (onSwitchChange) {
      onSwitchChange(name, checked);
    }
  };

  const handlePhoneChange = (name: string, phone: string) => {
    setFormData({
      ...formData,
      [name]: phone, // Update phone number field
    });
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await validationSchema.validate(formData, { abortEarly: false });
      setErrors({});
      if (onSubmit) {
        onSubmit(formData);
      }
    } catch (validationErrors) {
      if (validationErrors instanceof Yup.ValidationError) {
        const formErrors: { [key: string]: string } = {};
        validationErrors.inner.forEach((error: Yup.ValidationError) => {
          if (error.path) {
            formErrors[error.path] = error.message;
          }
        });
        setErrors(formErrors);
      }
    }
  };

  return (
    <form className="form-wrapper" onSubmit={handleSubmit}>
      <h4 className="title">{title}</h4>
      <div className="generic-form-main-container">
        {fields.map((field, index) => (
          <React.Fragment key={field.name}>
            {/* Render the <hr /> as a separate element before "section" fields */}
            {field.type === "section" && index !== 0 && <hr />}

            <div
              className={`form-group ${field.type === "section"
                ? "section-group"
                : field.type === "link"
                  ? "link-group"
                  : ""
                }`}
            >
              {/* Render title for "section" fields */}
              {field.type === "section" ? (
                <h4 className="title">{field.label}</h4>
              ) : field.type === "link" ? (
                // Render the link field as a button
                <button
                  type="button"
                  className="link-button"
                  onClick={field.onClick} // Use the onClick handler provided in the field
                >
                  {field.label}
                </button>
              ) : (
                <>
                  {/* Render the label for non-section fields */}
                  {!(field.name === "center" && formData.role === "Admin") && (
                    <label>{field.label}</label>
                  )}

                  {/* Render the appropriate input field */}
                  {field.type === "select" && field?.options?.length ? (
                    field.name === "center" && formData.role === "Admin" ? null : (
                      <Dropdown
                        handleFormChange={(selectedOption: Option) =>
                          handleSelectChange(field.name, selectedOption)
                        }
                        dropdownOptions={field.options}
                        placeholder={"Select"}
                        defaultOption={
                          field.initialValue
                            ? { label: field.initialValue, value: field.initialValue }
                            : undefined
                        }
                        disabled={field.disabled}
                      />
                    )
                  ) : field.type === "switch" ? (
                    <Switch
                      onChange={(checked) =>
                        handleSwitchChange(field.name, checked)
                      }
                      checked={!!formData[field.name]}
                    />
                  ) :
                    field.type === "phoneInput" ? (
                      <PhoneNumberInput
                        onChange={(phone = "") => handlePhoneChange(field.name, phone)}
                        value={formData[field.name]}
                        defaultCountry="il"
                      />
                    ) : (
                      <input
                        type={field.type}
                        name={field.name}
                        value={formData[field.name] || ""}
                        onChange={handleChange}
                        className="form-control"
                        disabled={field.disabled}
                      />
                    )}

                  {/* Validation error message */}
                  {errors[field.name] && (
                    <span className="error-message">{errors[field.name]}</span>
                  )}
                </>
              )}
            </div>
          </React.Fragment>
        ))}

      </div>
      {children}
      <div className="button-container">
        <div className="back-container">
          <button
            type="button"
            onClick={backButtonAction}
            className="back-button-main-container btn btn-primary"
          >
            <svgs.icons.BackIcon />
            <span className="text">{backButtonText}</span>
          </button>
        </div>
        {buttonText && (
          <div className="save-container">
            <button
              type="submit"
              className="button-main-container btn btn-primary"
            >
              {buttonText}
            </button>
          </div>
        )}
      </div>
    </form>
  );
};

export default GenericForm;
